<template>
   <button
      class="locale-modal-btn"
      data-cy="lang-modal-toggle-btn"
      aria-haspopup="true"
      :title="$t('system.locale_dialog.locale_button_aria', { currentChoice: localeStore.currentLocale?.name })"
      :aria-label="$t('system.locale_dialog.locale_button_aria', { currentChoice: localeStore.currentLocale?.name })"
      @click="onModalOpen"
   >
      <span class="flex size-8 items-center justify-center" aria-hidden="true">
         <IconLocale />
      </span>
   </button>

   <Modal
      v-if="localeModalVisible"
      id="locale-select"
      :title="$t('system.locale_dialog.title')"
      :pill="{ text: $t('system.locale_dialog.tag'), theme: Theme.Sand }"
      @close="onLocaleModalClose"
      @confirm="onLocaleUpdate"
   >
      <p class="text-center">{{ $t("system.locale_dialog.text") }}</p>
      <fieldset class="mt-4 rounded-lg border border-grey-10 p-6" data-cy="locale-radio-group">
         <RadioInput
            v-for="locale in localeStore.pageSupportedLocales"
            :id="`locale-nav-${locale.id}`"
            :key="`locale-nav-${locale.id}`"
            group="locales-nav"
            data-cy="locale-radio-input"
            :isSelected="locale.id === modalSelectedLocaleRadioId"
            :label="locale.name"
            :value="locale.id"
            @option-selected="onLocaleSelection"
         />
      </fieldset>
   </Modal>
</template>
<script setup lang="ts">
import { replaceLocaleInUrl } from "@/js/helpers/url.utils";
import { useLocaleStore } from "@/stores/localeStore";
import { Theme } from "@/types";

const $emit = defineEmits(["close-mobile-menu"]);
const localeStore = useLocaleStore();
const $route = useRoute();

const localeModalVisible = ref(false);
const modalSelectedLocaleRadioId = ref(localeStore.currentLocale?.id);

function onModalOpen() {
   localeModalVisible.value = true;
}

function onLocaleSelection(id: string): void {
   // dynamicaly update modalSelectedLocaleRadioId value, as user changes locales radio selection (before confirmation)
   modalSelectedLocaleRadioId.value = id;
}

function onLocaleUpdate(): void {
   // after confirm, hide the modal
   localeModalVisible.value = false;

   // close the mobile menu
   $emit("close-mobile-menu");

   // if the current locale is up-to-date, do nothing
   if (modalSelectedLocaleRadioId.value !== localeStore.currentLocale.id) {
      // otherwise redirect to the current locale - We reload the page to ensure that any new locale content is re-fetched
      const newUrl = replaceLocaleInUrl($route.params?.locale, modalSelectedLocaleRadioId.value);
      window.location.href = newUrl;
   }
}

function onLocaleModalClose(): void {
   // restore to the current locale id, so the selection without confirmation is not saved
   modalSelectedLocaleRadioId.value = localeStore.currentLocale.id;

   // close the modal
   localeModalVisible.value = false;
}
</script>

<style scoped lang="scss">
.locale-modal-btn {
   color: var(--black);
   aspect-ratio: 1/1;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   border-radius: var(--size-base-6);
   width: var(--size-base-40);
   height: var(--size-base-40);
   margin-left: var(--size-base-8);
   &:hover {
      background-color: var(--grey-10);
   }
}
@media (min-width: 48em) {
   .locale-modal-btn {
      margin-left: 0;
   }
}

@media print {
   .locale-modal-btn {
      display: none;
   }
}
</style>
